'use client'

import ChatPopupButton, { ChatPopupProvider } from '@/components/chat-popup'
import { useServiceLabel } from '@/lib/hooks/use-service-label'
import DealItemEditor from '@/components/sales/DealItemEditor'
import {
    createDeal,
    CreateDealData,
    CreateDealItemData,
    Deal,
    DEAL_STATUS_COLORS,
    DEAL_STATUS_LABELS,
    DealFilters,
    DealStatus,
    getDeals,
} from '@/lib/api/sales/deals-api'
import {
    getWalletLeadCategoryLabels,
    WalletLeadCategory,
} from '@/lib/api/sales/wallets-api'
import {
    getLeads,
    Lead, LEAD_SENTIMENT_LABELS, LeadSentimentType
} from '@/lib/api/sales/leads-api'
import {
    getPipelines,
    SalesPipeline
} from '@/lib/api/sales/pipelines-api'
import { useResourcePermissions } from '@/lib/hooks/use-resource-permissions'
import { useResetPageOnChange, useUrlPage } from '@/lib/hooks/use-url-page'
import { SocketContext } from '@/lib/providers/socket-provider'
import { useAuth } from '@/store/hooks'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { useContext, useEffect, useMemo, useState } from 'react'
import {
    MdAdd,
    MdAttachMoney,
    MdFilterList,
    MdHandshake,
} from 'react-icons/md'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import PaginationControls from '@/components/pagination/PaginationControls'

export default function DealsListPage() {
    const walletLeadCategoryLabels = getWalletLeadCategoryLabels(
        useServiceLabel()
    )
    const { user } = useAuth()
    const { socket } = useContext(SocketContext)
    const navigate = useNavigate()
    const { canRead, canCreate } = useResourcePermissions('sales', 'deals')

    const [deals, setDeals] = useState<Deal[]>([])
    const [total, setTotal] = useState(0)
    const [page, setPage] = useUrlPage()
    const LIMIT = 50
    const [pipelines, setPipelines] = useState<SalesPipeline[]>([])
    const [leads, setLeads] = useState<Lead[]>([])
    const [loading, setLoading] = useState(true)

    // Filters
    const [filters, setFilters] = useState<DealFilters>({})
    const [showFilters, setShowFilters] = useState(false)

    // Create dialog
    const [showCreateModal, setShowCreateModal] = useState(false)
    const [createForm, setCreateForm] = useState<CreateDealData>({
        title: '',
        pipelineId: 0,
        stageId: 0,
        leadId: 0,
        category: 'CONSULTATION',
    })
    const [createItems, setCreateItems] = useState<CreateDealItemData[]>([])
    const [creating, setCreating] = useState(false)
    const [leadSearch, setLeadSearch] = useState('')

    useEffect(() => {
        loadPipelines()
        loadLeads()
    }, [])

    useResetPageOnChange(setPage, [filters])

    useEffect(() => {
        loadDeals()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters, page])

    const loadDeals = async () => {
        setLoading(true)
        try {
            const response = await getDeals({ ...filters, page, limit: LIMIT })
            if (response.success && response.result) {
                setDeals(response.result.items)
                setTotal(response.result.total)
            } else {
                toast.error(response.error || 'Erro ao carregar negócios')
            }
        } catch {
            toast.error('Erro ao carregar negócios')
        } finally {
            setLoading(false)
        }
    }

    // Atualização em tempo real via WebSocket
    // O funil é da ORGANIZAÇÃO: o canal também. O backend ainda publica no canal por
    // empresa, mas ele não vê a oportunidade da triagem, que não tem filial.
    useEffect(() => {
        if (!socket || !user?.organizationId) return
        const event = `deal-updated-org-${user.organizationId}`
        const handler = () => loadDeals()
        socket.on(event, handler)
        return () => { socket.off(event, handler) }
    }, [socket, user?.organizationId])

    const loadPipelines = async () => {
        try {
            const response = await getPipelines()
            if (response.success && response.result) {
                setPipelines(response.result)
            }
        } catch {
            // Silently fail
        }
    }

    const loadLeads = async () => {
        try {
            const response = await getLeads({ limit: 200 })
            if (response.success && response.result) {
                setLeads(response.result.items)
            }
        } catch {
            // Silently fail
        }
    }

    const selectedPipeline = useMemo(() => {
        return pipelines.find(p => p.id === filters.pipelineId) || null
    }, [filters.pipelineId, pipelines])

    const filteredStages = useMemo(() => {
        if (!selectedPipeline) return []
        return (selectedPipeline.stages || []).sort((a, b) => a.sortOrder - b.sortOrder)
    }, [selectedPipeline])

    const createPipelineStages = useMemo(() => {
        const p = pipelines.find(p => p.id === createForm.pipelineId)
        return (p?.stages || []).sort((a, b) => a.sortOrder - b.sortOrder)
    }, [createForm.pipelineId, pipelines])

    const filteredLeads = useMemo(() => {
        if (!leadSearch) return leads.slice(0, 20)
        const search = leadSearch.toLowerCase()
        return leads
            .filter(l =>
                l.name.toLowerCase().includes(search) ||
                l.phoneNumber.includes(search) ||
                (l.email || '').toLowerCase().includes(search)
            )
            .slice(0, 20)
    }, [leads, leadSearch])

    const totalValue = useMemo(() => {
        return deals.reduce((sum, d) => sum + (d.totalValue || 0), 0)
    }, [deals])

    const formatBRL = (cents: number) =>
        new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(cents / 100)

    const handleCreate = async () => {
        if (!createForm.title || !createForm.pipelineId || !createForm.stageId || !createForm.leadId) {
            toast.error('Preencha todos os campos obrigatórios')
            return
        }
        setCreating(true)
        try {
            const response = await createDeal({
                ...createForm,
                items: createItems.length > 0 ? createItems : undefined,
            })
            if (response.success) {
                toast.success('Oportunidade criada com sucesso')
                setShowCreateModal(false)
                resetCreateForm()
                loadDeals()
            } else {
                toast.error(response.error || 'Erro ao criar oportunidade')
            }
        } catch {
            toast.error('Erro ao criar oportunidade')
        } finally {
            setCreating(false)
        }
    }

    const resetCreateForm = () => {
        setCreateForm({ title: '', pipelineId: 0, stageId: 0, leadId: 0, category: 'CONSULTATION' })
        setCreateItems([])
        setLeadSearch('')
    }

    const addItem = () => {
        setCreateItems([...createItems, { name: '', unitPrice: 0, quantity: 1 }])
    }

    return (
        <ChatPopupProvider>
            <div className="p-6 space-y-6">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div>
                        <h1 className="text-2xl font-bold">Oportunidades</h1>
                        <div className="flex items-center gap-4 mt-1 text-sm opacity-70">
                            <span>{deals.length} oportunidade(s)</span>
                            <span className="flex items-center gap-1">
                                <MdAttachMoney size={14} /> Total: {formatBRL(totalValue)}
                            </span>
                        </div>
                    </div>
                    <div className="flex gap-2">
                        <button
                            className="btn btn-sm btn-outline"
                            onClick={() => setShowFilters(!showFilters)}
                        >
                            <MdFilterList size={16} /> Filtros
                        </button>
                        {canCreate && (
                            <button
                                className="btn btn-sm btn-secondary"
                                onClick={() => setShowCreateModal(true)}
                            >
                                <MdAdd size={16} /> Criar Oportunidade
                            </button>
                        )}
                    </div>
                </div>

                {/* Filters */}
                {showFilters && (
                    <div className="card bg-base-100 shadow-sm">
                        <div className="card-body">
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                                <div className="form-control">
                                    <label className="label"><span className="label-text">Funil</span></label>
                                    <select
                                        className="select select-bordered select-sm"
                                        value={filters.pipelineId || ''}
                                        onChange={e =>
                                            setFilters({
                                                ...filters,
                                                pipelineId: e.target.value ? parseInt(e.target.value) : undefined,
                                                stageId: undefined,
                                            })
                                        }
                                    >
                                        <option value="">Todos</option>
                                        {pipelines.map(p => (
                                            <option key={p.id} value={p.id}>{p.name}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="form-control">
                                    <label className="label"><span className="label-text">Etapa</span></label>
                                    <select
                                        className="select select-bordered select-sm"
                                        value={filters.stageId || ''}
                                        onChange={e =>
                                            setFilters({
                                                ...filters,
                                                stageId: e.target.value ? parseInt(e.target.value) : undefined,
                                            })
                                        }
                                        disabled={!filters.pipelineId}
                                    >
                                        <option value="">Todas</option>
                                        {filteredStages.map(s => (
                                            <option key={s.id} value={s.id}>{s.name}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="form-control">
                                    <label className="label"><span className="label-text">Status</span></label>
                                    <select
                                        className="select select-bordered select-sm"
                                        value={filters.status || ''}
                                        onChange={e =>
                                            setFilters({
                                                ...filters,
                                                status: e.target.value as DealStatus || undefined,
                                            })
                                        }
                                    >
                                        <option value="">Todos</option>
                                        {Object.entries(DEAL_STATUS_LABELS).map(([val, label]) => (
                                            <option key={val} value={val}>{label}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="form-control">
                                    <label className="label"><span className="label-text">Data Início</span></label>
                                    <input
                                        type="date"
                                        className="input input-bordered input-sm"
                                        value={filters.startDate || ''}
                                        onChange={e =>
                                            setFilters({ ...filters, startDate: e.target.value || undefined })
                                        }
                                    />
                                </div>
                                <div className="form-control">
                                    <label className="label"><span className="label-text">Data Fim</span></label>
                                    <input
                                        type="date"
                                        className="input input-bordered input-sm"
                                        value={filters.endDate || ''}
                                        onChange={e =>
                                            setFilters({ ...filters, endDate: e.target.value || undefined })
                                        }
                                    />
                                </div>
                            </div>
                            <div className="flex justify-end mt-2">
                                <button
                                    className="btn btn-ghost btn-xs"
                                    onClick={() => setFilters({})}
                                >
                                    Limpar Filtros
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Loading */}
                {loading && (
                    <div className="flex justify-center py-12">
                        <span className="loading loading-spinner loading-lg" />
                    </div>
                )}

                {/* Deals Cards */}
                {!loading && (
                    <>
                        {deals.length === 0 ? (
                            <div className="text-center py-16 opacity-50">
                                <MdHandshake size={56} className="mx-auto mb-3" />
                                <p className="text-lg">Nenhuma oportunidade encontrada</p>
                                <p className="text-sm mt-1">Crie sua primeira oportunidade para começar</p>
                            </div>
                        ) : (
                            <div className="space-y-3">
                                {deals.map(deal => (
                                    <div key={deal.id} className="card bg-base-100 shadow-sm">
                                        <div className="card-body p-4">
                                            {/* Header row */}
                                            <div className="flex items-start justify-between">
                                                <div className="flex items-center gap-3 flex-1 min-w-0">
                                                    <MdHandshake size={20} className="text-secondary shrink-0" />
                                                    <div className="min-w-0">
                                                        <h3 className="font-semibold truncate">{deal.title}</h3>
                                                        <p className="text-sm opacity-60 truncate">{deal.lead?.name || '-'}</p>
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-2 shrink-0">
                                                    {(deal as any)?.chat?.id && (
                                                        <ChatPopupButton
                                                            chatId={(deal as any).chat.id}
                                                            chatStatus={(deal as any).chat.status}
                                                        />
                                                    )}
                                                    <button
                                                        className="btn btn-neutral btn-xs"
                                                        title="Ver detalhes"
                                                        onClick={() => navigate(`/sales/deals/${deal.id}`)}
                                                    >
                                                        Detalhes
                                                    </button>
                                                </div>
                                            </div>

                                            {/* Badges row */}
                                            <div className="flex flex-wrap gap-2 mt-2">
                                                <span className="badge badge-sm" style={{ backgroundColor: deal.stage?.color || undefined, color: deal.stage?.color ? '#fff' : undefined }}>
                                                    {deal.stage?.name || '-'}
                                                </span>
                                                <span className="badge badge-sm badge-outline">{deal.pipeline?.name || '-'}</span>
                                                <span className={`badge badge-sm ${DEAL_STATUS_COLORS[deal.status]}`}>
                                                    {DEAL_STATUS_LABELS[deal.status]}
                                                </span>
                                                {(deal as any).currentSentiment && (
                                                    <span className="badge badge-sm badge-outline">
                                                        {LEAD_SENTIMENT_LABELS[(deal as any).currentSentiment as LeadSentimentType] || (deal as any).currentSentiment}
                                                    </span>
                                                )}
                                            </div>

                                            {/* Info row */}
                                            <div className="flex items-center gap-4 mt-2 text-xs opacity-60">
                                                <span className="font-bold text-secondary text-sm">{formatBRL(deal.totalValue || 0)}</span>
                                                {deal.assignedUser && <span>{deal.assignedUser.name}</span>}
                                                <span>{format(new Date(deal.createdAt), 'dd/MM/yyyy', { locale: ptBR })}</span>
                                                {deal.expectedCloseDate && <span>Previsão: {format(new Date(deal.expectedCloseDate), 'dd/MM/yyyy', { locale: ptBR })}</span>}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                        <PaginationControls
                            page={page}
                            limit={LIMIT}
                            total={total}
                            loading={loading}
                            onPageChange={setPage}
                            label="oportunidade"
                        />
                    </>
                )}

                {/* Create Modal */}
                {showCreateModal && (
                    <dialog className="modal modal-open">
                        <div className="modal-box !max-w-3xl max-h-[85vh] overflow-y-auto">
                            <h3 className="font-bold text-lg mb-4">Criar Oportunidade</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="form-control col-span-full">
                                    <label className="label"><span className="label-text">Título *</span></label>
                                    <input
                                        type="text"
                                        className="input input-bordered w-full"
                                        value={createForm.title}
                                        onChange={e => setCreateForm({ ...createForm, title: e.target.value })}
                                        placeholder="Nome da oportunidade"
                                    />
                                </div>
                                <div className="form-control col-span-full">
                                    <label className="label"><span className="label-text">Descrição</span></label>
                                    <textarea
                                        className="textarea textarea-bordered w-full"
                                        rows={2}
                                        value={createForm.description || ''}
                                        onChange={e => setCreateForm({ ...createForm, description: e.target.value })}
                                    />
                                </div>
                                <div className="form-control">
                                    <label className="label"><span className="label-text">Funil *</span></label>
                                    <select
                                        className="select select-bordered"
                                        value={createForm.pipelineId || ''}
                                        onChange={e =>
                                            setCreateForm({
                                                ...createForm,
                                                pipelineId: parseInt(e.target.value) || 0,
                                                stageId: 0,
                                            })
                                        }
                                    >
                                        <option value="">Selecione</option>
                                        {pipelines.filter(p => p.active).map(p => (
                                            <option key={p.id} value={p.id}>{p.name}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="form-control">
                                    <label className="label"><span className="label-text">Etapa *</span></label>
                                    <select
                                        className="select select-bordered"
                                        value={createForm.stageId || ''}
                                        onChange={e =>
                                            setCreateForm({ ...createForm, stageId: parseInt(e.target.value) || 0 })
                                        }
                                        disabled={!createForm.pipelineId}
                                    >
                                        <option value="">Selecione</option>
                                        {createPipelineStages.map(s => (
                                            <option key={s.id} value={s.id}>{s.name}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="form-control">
                                    <label className="label"><span className="label-text">Categoria *</span></label>
                                    <select
                                        className="select select-bordered"
                                        value={createForm.category}
                                        onChange={e =>
                                            setCreateForm({ ...createForm, category: e.target.value as WalletLeadCategory })
                                        }
                                    >
                                        {(['CONSULTATION', 'PROCEDURE', 'EXAM'] as WalletLeadCategory[]).map(c => (
                                            <option key={c} value={c}>{walletLeadCategoryLabels[c]}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="form-control col-span-full">
                                    <label className="label"><span className="label-text">Lead *</span></label>
                                    <input
                                        type="text"
                                        className="input input-bordered"
                                        placeholder="Buscar lead por nome, telefone ou e-mail..."
                                        value={leadSearch}
                                        onChange={e => setLeadSearch(e.target.value)}
                                    />
                                    {leadSearch && (
                                        <div className="max-h-40 overflow-y-auto border rounded mt-1 bg-base-100">
                                            {filteredLeads.length === 0 ? (
                                                <div className="p-2 text-sm opacity-50">Nenhum lead encontrado</div>
                                            ) : (
                                                filteredLeads.map(l => (
                                                    <div
                                                        key={l.id}
                                                        className={`p-2 hover:bg-base-200 cursor-pointer text-sm ${createForm.leadId === l.id ? 'bg-secondary/10' : ''}`}
                                                        onClick={() => {
                                                            setCreateForm({ ...createForm, leadId: l.id })
                                                            setLeadSearch(l.name)
                                                        }}
                                                    >
                                                        <span className="font-medium">{l.name}</span>
                                                        <span className="opacity-60 ml-2">{l.phoneNumber}</span>
                                                    </div>
                                                ))
                                            )}
                                        </div>
                                    )}
                                    {createForm.leadId > 0 && (
                                        <div className="text-xs text-success mt-1">
                                            Lead selecionado: #{createForm.leadId}
                                        </div>
                                    )}
                                </div>
                                <div className="form-control">
                                    <label className="label"><span className="label-text">Previsão de Fechamento</span></label>
                                    <input
                                        type="date"
                                        className="input input-bordered"
                                        value={createForm.expectedCloseDate || ''}
                                        onChange={e =>
                                            setCreateForm({ ...createForm, expectedCloseDate: e.target.value || undefined })
                                        }
                                    />
                                </div>
                                <div className="form-control">
                                    <label className="label"><span className="label-text">Tags (separadas por vírgula)</span></label>
                                    <input
                                        type="text"
                                        className="input input-bordered"
                                        value={(createForm.tags || []).join(', ')}
                                        onChange={e =>
                                            setCreateForm({
                                                ...createForm,
                                                tags: e.target.value.split(',').map(t => t.trim()).filter(Boolean),
                                            })
                                        }
                                    />
                                </div>
                            </div>

                            {/* Items */}
                            <div className="divider">Itens</div>
                            <DealItemEditor
                                items={createItems}
                                onChange={setCreateItems}
                            />

                            <div className="modal-action">
                                <button
                                    className="btn btn-ghost"
                                    onClick={() => {
                                        setShowCreateModal(false)
                                        resetCreateForm()
                                    }}
                                >
                                    Cancelar
                                </button>
                                <button
                                    className="btn btn-secondary"
                                    onClick={handleCreate}
                                    disabled={creating}
                                >
                                    {creating && <span className="loading loading-spinner loading-sm" />}
                                    Criar
                                </button>
                            </div>
                        </div>
                        <form method="dialog" className="modal-backdrop">
                            <button onClick={() => { setShowCreateModal(false); resetCreateForm() }}>close</button>
                        </form>
                    </dialog>
                )}
            </div>
        </ChatPopupProvider>
    )
}
